import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconRefresh, IconSearch } from "@tabler/icons-react";
import type { DeletionIntake } from "@/api/accountDeletion";
import { PageHeader } from "@/components/PageHeader";
import { Timestamp } from "@/components/Timestamp";
import { accountDeletionPath } from "@/routes/paths";
import { isPast } from "@/lib/datetime";
import { IntakeDialog } from "./IntakeDialog";
import { QueueFooter, QueueState } from "./QueueParts";
import { localDateTime, STATE_LABELS, STEP_LABELS } from "./presentation";
import type { AccountDeletionQueueViewProps, DeletionStateFilter, DeletionTab } from "./types";

export function DeletionRequestLink({ id }: { id: string }) {
  return (
    <Anchor component={Link} to={accountDeletionPath(id)} size="sm" ff="monospace">
      {id}
    </Anchor>
  );
}

export function ResponseTarget({
  value,
  completed = false,
}: {
  value: string;
  completed?: boolean;
}) {
  return (
    <Stack gap={2}>
      <Timestamp value={value} absolute />
      {!completed && isPast(value) ? (
        <Text size="xs" c="orange">
          First reply target passed
        </Text>
      ) : null}
    </Stack>
  );
}

export function AccountDeletionQueueView(props: AccountDeletionQueueViewProps) {
  const [lookup, setLookup] = useState("");
  const [selection, setSelection] = useState<{
    intake: DeletionIntake;
    mode: "verify" | "dismiss";
  } | null>(null);
  const [before, setBefore] = useState(localDateTime(props.retentionBefore));
  const busy = props.actionPending;
  const validBefore = Number.isFinite(new Date(before).getTime());

  return (
    <>
      <PageHeader
        title="Account deletion"
        subtitle="Manage deletion requests and follow up on data kept under a policy."
        actions={
          <Button
            variant="default"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={props.onRefresh}
            disabled={busy}
          >
            Refresh
          </Button>
        }
      />
      <Tabs
        value={props.tab}
        onChange={(tab) => {
          if (tab) props.onTabChange(tab as DeletionTab);
        }}
        keepMounted={false}
      >
        <Tabs.List mb="lg">
          <Tabs.Tab value="requests">Requests</Tabs.Tab>
          <Tabs.Tab value="intakes">Web intakes</Tabs.Tab>
          <Tabs.Tab value="retention">Retention</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="requests">
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="lg" align="flex-end">
              <Select
                label="Status"
                value={props.stateFilter}
                onChange={(value) => {
                  if (value) props.onStateFilterChange(value as DeletionStateFilter);
                }}
                data={[
                  { value: "active", label: "Active requests" },
                  { value: "all", label: "All requests" },
                  ...Object.entries(STATE_LABELS).map(([value, label]) => ({ value, label })),
                ]}
                w={230}
              />
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (lookup.trim()) props.onLookup(lookup.trim());
                }}
              >
                <Group align="flex-end" gap="xs">
                  <TextInput
                    label="Open a request"
                    placeholder="Request or account ID"
                    value={lookup}
                    onChange={(e) => setLookup(e.currentTarget.value)}
                    leftSection={<IconSearch size={15} />}
                    w={300}
                  />
                  <Button type="submit" variant="default" disabled={!lookup.trim()}>
                    Open
                  </Button>
                </Group>
              </form>
            </Group>
            <QueueState queue={props.requests} emptyTitle="No matching requests" />
            {props.requests.items.length ? (
              <Table.ScrollContainer minWidth={800}>
                <Table highlightOnHover verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Request / account</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Received</Table.Th>
                      <Table.Th>First reply target</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {props.requests.items.map((request) => (
                      <Table.Tr key={request.id}>
                        <Table.Td>
                          <DeletionRequestLink id={request.id} />
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            tt="none"
                            color={
                              request.state === "completed"
                                ? "teal"
                                : request.state === "awaitingUserAction"
                                  ? "orange"
                                  : "indigo"
                            }
                          >
                            {STATE_LABELS[request.state]}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Timestamp value={request.requestedAt} absolute />
                        </Table.Td>
                        <Table.Td>
                          <ResponseTarget
                            value={request.responseDueAt}
                            completed={request.state === "completed"}
                          />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : null}
            <QueueFooter queue={props.requests} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="intakes">
          <Card withBorder padding="lg">
            <Text size="sm" c="dimmed" mb="md">
              Verify ownership before linking an account. Contact addresses remain private to this
              queue.
            </Text>
            <QueueState queue={props.intakes} emptyTitle="No unverified web intakes" />
            {props.intakes.items.length ? (
              <Table.ScrollContainer minWidth={900}>
                <Table highlightOnHover verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Intake</Table.Th>
                      <Table.Th>Unverified contact</Table.Th>
                      <Table.Th>Received</Table.Th>
                      <Table.Th>First reply target</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {props.intakes.items.map((intake) => (
                      <Table.Tr key={intake.id}>
                        <Table.Td>
                          <Text size="xs" ff="monospace">
                            {intake.id}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{intake.email ?? "Contact removed"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Timestamp value={intake.receivedAt} />
                        </Table.Td>
                        <Table.Td>
                          <ResponseTarget value={intake.responseDueAt} />
                        </Table.Td>
                        <Table.Td>
                          <Tooltip
                            label="Your role cannot update deletion intakes."
                            disabled={props.canAct}
                          >
                            <Group gap="xs" wrap="nowrap">
                              <Button
                                size="xs"
                                variant="default"
                                disabled={
                                  !props.canAct ||
                                  busy ||
                                  props.intakes.loading ||
                                  !!props.intakes.error
                                }
                                onClick={() => setSelection({ intake, mode: "verify" })}
                              >
                                Link account
                              </Button>
                              <Button
                                size="xs"
                                variant="light"
                                color="red"
                                disabled={
                                  !props.canAct ||
                                  busy ||
                                  props.intakes.loading ||
                                  !!props.intakes.error
                                }
                                onClick={() => setSelection({ intake, mode: "dismiss" })}
                              >
                                Dismiss
                              </Button>
                            </Group>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : null}
            <QueueFooter queue={props.intakes} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="retention">
          <Card withBorder padding="lg">
            <Group justify="space-between" align="flex-end" mb="lg">
              <Text size="sm" c="dimmed" maw={420}>
                Current retained outcomes, including completed requests. Verify the next action in
                the restricted case and record a replacement review.
              </Text>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (validBefore) props.onRetentionBeforeChange(new Date(before).toISOString());
                }}
              >
                <Group gap="xs" align="flex-end">
                  <TextInput
                    type="datetime-local"
                    label="Due before (local time)"
                    value={before}
                    onChange={(e) => setBefore(e.currentTarget.value)}
                  />
                  <Button type="submit" variant="default" disabled={!validBefore}>
                    Apply
                  </Button>
                </Group>
              </form>
            </Group>
            <QueueState
              queue={props.retention}
              emptyTitle="No retained outcomes due before this date"
            />
            {props.retention.items.length ? (
              <Table.ScrollContainer minWidth={850}>
                <Table highlightOnHover verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Request</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Policy</Table.Th>
                      <Table.Th>Expiry / review deadline</Table.Th>
                      <Table.Th>Evidence reference</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {props.retention.items.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <DeletionRequestLink id={item.requestId} />
                        </Table.Td>
                        <Table.Td>{STEP_LABELS[item.step]}</Table.Td>
                        <Table.Td>
                          <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                            {item.retentionPolicy ?? "Missing policy"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={2}>
                            <Timestamp value={item.retentionUntil} absolute />
                            {isPast(item.retentionUntil) ? (
                              <Text size="xs" c="orange">
                                Review due
                              </Text>
                            ) : null}
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                            {item.evidenceReference}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : null}
            <QueueFooter queue={props.retention} />
          </Card>
        </Tabs.Panel>
      </Tabs>
      {selection ? (
        <IntakeDialog
          intake={selection.intake}
          mode={selection.mode}
          currentUpdatedAt={
            props.intakes.items.find((item) => item.id === selection.intake.id)?.updatedAt ?? null
          }
          canAct={props.canAct && !props.intakes.loading && !props.intakes.error}
          pending={props.actionPending}
          onClose={() => setSelection(null)}
          onVerify={props.onVerifyIntake}
          onDismiss={props.onDismissIntake}
        />
      ) : null}
    </>
  );
}
